import {
  MoveUpRight,
  MoveDownLeft,
  AlertCircle,
  Wallet,
  History,
  Gift,
  Loader2,
  Repeat
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import AssetCard from '@/components/AssetCard'
import AnnouncementBanner from '@/components/AnnouncementBanner'
import MessageDialog, { MessageDialogType } from '@/components/MessageDialog'
import LatestTransaction from '@/components/LatestTransaction'
import TransactionDetailDialog from '@/components/dialogs/TransactionDetailDialog'
import { useBalance } from '@/hooks/useBalance'
import { useTransactions } from '@/hooks/useTransactions'
import { useWallet } from '@/hooks/useWallet'
import Skeleton from '@/components/common/Skeleton'

import { useCurrency } from '@/hooks/useCurrency'
import { getNetwork } from '@/config/networks'
import { isSwapEnabled } from '@/config/app'
import { Transaction } from '@/types/transaction.types'
import {
  airdropService,
  RewardPayout
} from '@/services/airdrop/airdrop.service'

// With Swap enabled, mobile shows 3 actions as icon buttons with the label
// below (Swap is mobile-only; desktop already has it in the sidebar). From lg
// up, and whenever Swap is disabled, the 2 original pills are used.
const swapEnabled = isSwapEnabled()
const actionItemClass = swapEnabled
  ? 'flex flex-col items-center gap-2'
  : 'flex flex-col items-center gap-2 w-full lg:w-auto'
const actionButtonShape = swapEnabled
  ? 'w-14 h-14 lg:w-full lg:py-4 lg:px-8 lg:min-w-max rounded-2xl lg:rounded-full'
  : 'py-4 px-8 h-18 lg:h-14 min-w-max w-full rounded-full'
const actionLabelClass = swapEnabled ? 'hidden lg:block' : 'block'

const Dashboard = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [selectedTx, setSelectedTx] = useState<Transaction | null>(null)
  const { address } = useWallet()
  const {
    totalAllNetworksUSD,
    allNetworksLoaded,
    tokens,
    allNetworksTokens,
    loading: balanceLoading,
    error: balanceError
  } = useBalance()
  const { transactions, loading: txLoading, error: txError } = useTransactions()
  const { formatValue } = useCurrency()

  const [activeAirdropCode, setActiveAirdropCode] = useState<string | null>(
    null
  )
  const [failedPayout, setFailedPayout] = useState<{
    code: string
    name: string
  } | null>(null)
  const [claimingAirdrop, setClaimingAirdrop] = useState(false)
  const [airdropDialog, setAirdropDialog] = useState<{
    open: boolean
    description: string
    type: MessageDialogType
    portalButton?: boolean
    rewards: RewardPayout[]
  }>({
    open: false,
    description: '',
    type: 'info',
    rewards: []
  })

  useEffect(() => {
    airdropService.getActiveAirdropCode().then(setActiveAirdropCode)
  }, [])

  useEffect(() => {
    if (!address) return

    airdropService.getFailedPayoutAirdrop(address).then(setFailedPayout)
    // El poller del backend reintenta cada 10 min, así que 1 min alcanza
    // para que el banner aparezca sin recargar la página cuando se agota.
    const interval = setInterval(() => {
      airdropService.getFailedPayoutAirdrop(address).then(setFailedPayout)
    }, 60_000)
    return () => clearInterval(interval)
  }, [address])

  const handleClaimAirdrop = async (code: string | null) => {
    if (!code || !address || claimingAirdrop) return

    setClaimingAirdrop(true)
    const outcome = await airdropService.claimAirdrop(code, address)
    setFailedPayout(await airdropService.getFailedPayoutAirdrop(address))
    setClaimingAirdrop(false)

    // Se muestran TODAS las recompensas pagadas — un airdrop puede pagar más
    // de una moneda (ej. OMD + OMDB) en un mismo reclamo.
    const paid =
      outcome.rewards?.filter(
        (r) => r.payoutStatus === 'completed' && r.payoutTxHash
      ) ?? []
    const stillProcessing =
      (outcome.rewards?.length ?? 0) > 0 && paid.length === 0

    if (
      (outcome.result === 'claimed' || outcome.result === 'alreadyClaimed') &&
      stillProcessing
    ) {
      // El pago sigue procesándose — "ya reclamado" sería confuso acá.
      setAirdropDialog({
        open: true,
        description: t('floatingAd.claim_processing'),
        type: 'info',
        rewards: []
      })
    } else if (outcome.result === 'claimed') {
      setAirdropDialog({
        open: true,
        description: t('floatingAd.claim_success'),
        type: 'success',
        rewards: paid
      })
    } else if (outcome.result === 'alreadyClaimed') {
      setAirdropDialog({
        open: true,
        description: t('floatingAd.claim_already_claimed'),
        type: 'info',
        rewards: paid
      })
    } else if (outcome.result === 'walletNotLinked') {
      setAirdropDialog({
        open: true,
        description: t('floatingAd.claim_wallet_not_linked'),
        type: 'error',
        rewards: [],
        portalButton: true
      })
    } else if (outcome.result === 'ineligible') {
      setAirdropDialog({
        open: true,
        description: t('floatingAd.claim_ineligible'),
        type: 'error',
        rewards: []
      })
    } else {
      setAirdropDialog({
        open: true,
        description: t('floatingAd.claim_error'),
        type: 'error',
        rewards: []
      })
    }
  }

  const airdropDialogTitle =
    airdropDialog.type === 'success'
      ? t('floatingAd.claim_result_success_title')
      : airdropDialog.type === 'info'
        ? t('floatingAd.claim_result_info_title')
        : t('floatingAd.claim_result_error_title')

  // Obtener las últimas 6 transacciones
  const TransactionHistory = Array.isArray(transactions)
    ? [...transactions]
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
        .slice(0, 6)
    : []

  // OMDB, OMD y USDT siempre se muestran en "Mis Activos", aunque el balance sea 0
  const FEATURED_SYMBOLS = ['OMDB', 'OMD', 'USDT']

  // Activos destacados + cualquier otro con balance > 0 en todas las redes, ordenados por valor USD
  const mainAssets = allNetworksTokens
    .filter(
      (token) =>
        FEATURED_SYMBOLS.includes(token.symbol) ||
        parseFloat(token.balance || '0') > 0
    )
    .sort((a, b) => {
      const aValue = parseFloat(a.balance || '0') * (a.priceUSD || 0)
      const bValue = parseFloat(b.balance || '0') * (b.priceUSD || 0)
      return bValue - aValue
    })

  // allNetworksLoaded se pone en true apenas hay un total cacheado (para mostrar el balance
  // rápido), pero allNetworksTokens no se cachea y tarda un momento más en llegar. Sin este
  // chequeo extra, "Mis Activos" mostraba "no tienes activos" por un instante antes de que
  // lleguen los tokens reales.
  const assetsLoading = !allNetworksLoaded || allNetworksTokens.length === 0

  const formatAddress = (addr: string) => {
    if (!addr) return ''
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`
  }

  const getNativeSymbol = (networkId: string) => {
    return getNetwork(networkId)?.nativeCurrency.symbol || 'OMDB'
  }

  const getTimeAgo = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000)
    if (seconds < 60) return t('time.just_now')
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return t('time.m_ago', { count: minutes })
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return t('time.h_ago', { count: hours })
    return new Date(timestamp).toLocaleDateString()
  }

  return (
    <div className="flex flex-col h-full w-full gap-12">
      {/* ERROR MESSAGE */}
      {(balanceError || txError) && (
        <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-2xl flex items-center gap-3 text-red-500">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm font-medium">{balanceError || txError}</p>
        </div>
      )}

      {/* Se combinan en una sola tarjeta si coinciden pago fallido + airdrop activo distinto */}
      {failedPayout &&
      activeAirdropCode &&
      activeAirdropCode !== failedPayout.code ? (
        <AnnouncementBanner
          key={`airdrop-payout-failed-and-new-${failedPayout.code}`}
          icon={<AlertCircle className="w-5 h-5" />}
          title={t('floatingAd.payout_failed_title')}
          description={t('floatingAd.payout_failed_and_new_description', {
            airdropName: failedPayout.name
          })}
          actionLabel={t('floatingAd.payout_failed_action')}
          onAction={() => handleClaimAirdrop(failedPayout.code)}
          actionLoading={claimingAirdrop}
          secondaryActionLabel={t('floatingAd.claimAirdrop')}
          onSecondaryAction={() => handleClaimAirdrop(activeAirdropCode)}
          dismissible={false}
        />
      ) : failedPayout ? (
        // No se puede cerrar: solo desaparece cuando el reclamo se resuelve.
        <AnnouncementBanner
          key={`airdrop-payout-failed-${failedPayout.code}`}
          icon={<AlertCircle className="w-5 h-5" />}
          title={t('floatingAd.payout_failed_title')}
          description={t('floatingAd.payout_failed_description', {
            airdropName: failedPayout.name
          })}
          actionLabel={t('floatingAd.payout_failed_action')}
          onAction={() => handleClaimAirdrop(failedPayout.code)}
          actionLoading={claimingAirdrop}
          dismissible={false}
        />
      ) : activeAirdropCode ? (
        <AnnouncementBanner
          key={`airdrop-${activeAirdropCode}`}
          icon={<Gift className="w-5 h-5" />}
          title={t('floatingAd.title')}
          description={t('floatingAd.disclaimer')}
          actionLabel={t('floatingAd.claimAirdrop')}
          onAction={() => handleClaimAirdrop(activeAirdropCode)}
          actionLoading={claimingAirdrop}
        />
      ) : null}

      {/* BALANCE */}
      <div className="flex flex-col gap-2.5 mt-5 lg:mt-0">
        <p className="text-sm text-text-main font-normal uppercase tracking-wider">
          {t('dashboard.total_balance')}
        </p>
        {!allNetworksLoaded ? (
          <Skeleton className="h-14 w-64 mb-2" />
        ) : (
          <h2 className="text-5xl text-text-main font-extrabold">
            {formatValue(totalAllNetworksUSD)}
          </h2>
        )}

        <div className="flex flex-row items-center gap-2.5">
          {balanceLoading ? (
            <Skeleton className="h-5 w-48" />
          ) : (
            <>
              <span className="text-text-main font-medium">
                {tokens[0]?.balanceFormatted} {tokens[0]?.symbol}
              </span>
              <p className="text-text-secondary">•</p>
              <span
                className={`${(tokens[0]?.priceChange24h || 0) >= 0 ? 'text-success' : 'text-red-500'} font-medium`}
              >
                {(tokens[0]?.priceChange24h || 0) > 0 ? '+' : ''}
                {(tokens[0]?.priceChange24h || 0).toFixed(2)}%{' '}
                {t('dashboard.today')}
              </span>
            </>
          )}
        </div>

        {/* BUTTONS */}
        <div
          className={`flex pt-8 h-full max-w-lg ${swapEnabled ? 'justify-around' : 'justify-between'} lg:justify-normal gap-4`}
        >
          <div className={actionItemClass}>
            <button
              onClick={() => navigate('/send')}
              aria-label={t('dashboard.send')}
              className={`btn-gradient-fill-secondary shadow-sm ${actionButtonShape} flex ${swapEnabled ? 'justify-center lg:justify-start' : ''} gap-3 content-center place-items-center`}
            >
              <MoveUpRight className="w-6 h-6" />
              <p className={`${actionLabelClass} font-bold`}>{t('dashboard.send')}</p>
            </button>
            {swapEnabled && (
              <span className="lg:hidden text-sm font-semibold text-text-main">
                {t('dashboard.send')}
              </span>
            )}
          </div>

          <div className={actionItemClass}>
            <button
              onClick={() => navigate('/receive')}
              aria-label={t('dashboard.receive')}
              className={`shadow-sm ${actionButtonShape} bg-card flex justify-center gap-3 content-center place-items-center transition-opacity duration-200 ease-in-out hover:opacity-80`}
            >
              <MoveDownLeft className="w-6 h-6 icon-gradient-secondary" />
              <p className={`${actionLabelClass} font-bold secondary-gradient-text`}>
                {t('dashboard.receive')}
              </p>
            </button>
            {swapEnabled && (
              <span className="lg:hidden text-sm font-semibold text-text-main">
                {t('dashboard.receive')}
              </span>
            )}
          </div>

          {swapEnabled && (
            <div className={`${actionItemClass} lg:hidden`}>
              <button
                onClick={() => navigate('/swap')}
                aria-label={t('swap.title')}
                className={`shadow-sm ${actionButtonShape} bg-card flex justify-center content-center place-items-center transition-opacity duration-200 ease-in-out hover:opacity-80`}
              >
                <Repeat className="w-6 h-6 icon-gradient-secondary" />
              </button>
              <span className="text-sm font-semibold text-text-main">
                {t('swap.title')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* MY ASSETS */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-row justify-between items-center">
          <div className="flex items-center gap-2.5">
            <h2 className="text-text-main font-bold text-2xl">
              {t('dashboard.my_assets')}
            </h2>
            {assetsLoading && (
              <Loader2 className="w-4 h-4 animate-spin text-text-secondary" />
            )}
          </div>
          <span
            className="primary-gradient-text font-medium cursor-pointer transition-opacity duration-200 ease-in-out hover:opacity-80"
            onClick={() => navigate('/assets')}
          >
            {t('dashboard.view_all_assets')}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {assetsLoading ? (
            [1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48 w-full rounded-3xl" />
            ))
          ) : mainAssets.length === 0 ? (
            <div className="col-span-full py-20 text-center border-2 border-dashed border-text-main/10 rounded-3xl">
              <p className="text-text-secondary">{t('assets.no_assets')}</p>
            </div>
          ) : (
            mainAssets.map((token, index) => {
              const network = getNetwork(token.network || '')
              return (
                <AssetCard
                  key={token.network + token.address + token.symbol}
                  logoUrl={token.logoUrl}
                  icon={<Wallet className="icon-gradient" />}
                  name={token.name}
                  code={token.symbol}
                  value={Number(
                    parseFloat(token.balanceFormatted || '0').toFixed(6)
                  )}
                  usdValue={`${formatValue(parseFloat(token.balance || '0') * (token.priceUSD || 0))}`}
                  change={`${(token.priceChange24h || 0) > 0 ? '+' : ''}${(token.priceChange24h || 0).toFixed(2)}%`}
                  isPositive={(token.priceChange24h || 0) >= 0}
                  layout="grid"
                  network={token.network}
                  networkName={network?.name}
                  networkLogoUrl={network?.logoUrl}
                  borderColor={
                    index % 3 === 0
                      ? 'border-primary'
                      : index % 3 === 1
                        ? 'border-accent'
                        : 'border-tertiary'
                  }
                />
              )
            })
          )}
        </div>
      </div>

      {/* LATEST TRANSACTIONS */}
      <div className="flex flex-col gap-6">
        <h2 className="text-text-main font-bold text-2xl">
          {t('dashboard.latest_transactions')}
        </h2>
        <div className="flex flex-col gap-4">
          {txLoading ? (
            [1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))
          ) : TransactionHistory.length > 0 ? (
            TransactionHistory.map((tx) => (
              <LatestTransaction
                key={tx.hash}
                title={
                  tx.type === 'send'
                    ? t('dashboard.sent')
                    : t('dashboard.received')
                }
                type={tx.type}
                address={formatAddress(tx.type === 'send' ? tx.to : tx.from)}
                fullAddress={tx.type === 'send' ? tx.to : tx.from}
                amount={
                  (tx.type === 'send' ? '-' : '+') +
                  Number(tx.value || '0').toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 8
                  })
                }
                coin={tx.tokenSymbol || getNativeSymbol(tx.network)}
                time={getTimeAgo(tx.timestamp)}
                onClick={() => setSelectedTx(tx)}
              />
            ))
          ) : (
            <div className="py-10 text-center border-2 border-dashed border-text-main/10 rounded-3xl">
              <p className="text-text-secondary">
                {t('dashboard.no_transactions')}
              </p>
            </div>
          )}
        </div>
      </div>
      <TransactionDetailDialog
        open={!!selectedTx}
        tx={selectedTx}
        onClose={() => setSelectedTx(null)}
      />

      <MessageDialog
        open={airdropDialog.open}
        title={airdropDialogTitle}
        description={airdropDialog.description}
        type={airdropDialog.type}
        portalButton={airdropDialog.portalButton}
        onClose={() => setAirdropDialog((prev) => ({ ...prev, open: false }))}
      >
        {airdropDialog.rewards.length > 0 && (
          <div className="flex flex-col gap-1.5 w-full">
            {airdropDialog.rewards.map((r, i) => (
              <div
                key={`${r.currency}-${i}`}
                className="flex flex-wrap items-baseline justify-center gap-2 text-xs"
              >
                <span className="font-semibold text-text-main shrink-0">
                  {r.currency}
                </span>
                <span className="font-mono text-text-secondary break-all">
                  {r.payoutTxHash}
                </span>
              </div>
            ))}
          </div>
        )}
      </MessageDialog>
    </div>
  )
}

export default Dashboard
